import { cn } from '@tempo-io/sui-class-utils';
import { ExpandLess } from '@tempo-io/sui-icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import { useState } from 'react';
import type { LabelWithField } from '@/components/ui/swimlane/types';

export const DEFAULT_LABELS_TO_SHOW_STANDARD = 3;
export const DEFAULT_LABELS_TO_SHOW_COMPACT = 2;

function getChipOpacity(chipColor: string): React.CSSProperties {
  return {
    backgroundColor: `${chipColor}4C`, // suffix the color hex value with 4C to get 40% opacity
  };
}

export function Labels({
  labels = [],
  color,
  isCompact,
}: {
  labels?: LabelWithField[];
  color: string;
  isCompact?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(true);
  const sanitizedLabels = labels.filter((label) => Boolean(label?.value));

  const defaultLabelsToShow = isCompact
    ? DEFAULT_LABELS_TO_SHOW_COMPACT
    : DEFAULT_LABELS_TO_SHOW_STANDARD;

  if (sanitizedLabels.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-wrap gap-2', {
        nowrap: collapsed,
        'flex-wrap': !collapsed,
        'max-w-[200px]': isCompact,
        'max-w-[300px]': !isCompact,
      })}
      data-testid="label-list"
    >
      {sanitizedLabels
        .slice(0, collapsed ? defaultLabelsToShow : sanitizedLabels.length)
        .map((label) => (
          <Tooltip key={`${label.fieldName}:${label.value}`}>
            <TooltipTrigger asChild>
              <span
                className={cn(
                  'inline-flex items-center h-6 px-3 rounded-2xl whitespace-nowrap overflow-hidden text-ellipsis',
                  isCompact ? 'text-xs' : 'text-[0.8125rem]',
                  collapsed && 'flex-1 min-w-[1px] max-w-fit hover:w-full',
                )}
                style={getChipOpacity(color)}
                data-testid="list-item-label"
              >
                {label.value}
              </span>
            </TooltipTrigger>
            <TooltipContent>{label.fieldName}</TooltipContent>
          </Tooltip>
        ))}
      {sanitizedLabels.length > defaultLabelsToShow && (
        <button
          type="button"
          className={cn(
            'inline-flex items-center h-6 px-3 rounded-2xl whitespace-nowrap min-w-fit cursor-pointer border-none',
            isCompact ? 'text-xs' : 'text-[0.8125rem]',
          )}
          style={getChipOpacity(color)}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            `+${sanitizedLabels.length - defaultLabelsToShow}`
          ) : (
            <span className="inline-flex align-middle text-base">
              <ExpandLess size={16} />
            </span>
          )}
        </button>
      )}
    </div>
  );
}
